import FormSelect from "../../Common/FormSelect";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  checkForWalletMin,
  hasRoles,
  removeItemWithExpiry,
  showToast,
} from "../../../lib/utility";
import CloseButton from "../../Common/CloseButton";
import Modal from "../../Common/Modal";
import { updateUserBalance, userSelector, userListGet } from "../UserSlice";
import { useDispatch, useSelector } from "react-redux";
import { authSelector } from "../../Auth/AuthSlice";
import { middlewareUpdate, middleware } from "../../../redux/store";
import danger from "../../../assets/img/danger.svg";

import { AppIcons } from "../../../assets/icon/appIcons";
import useUserModuleAbac from "../../../hooks/useUserModuleAbac";

const BalanceUser = ({
  modal,
  closeModal,
  userData,
  id,
  addBalance = false,
  userDataGetOnce,
}) => {
  const { t } = useTranslation("user");
  const { canUpdateBalance } = useUserModuleAbac();
  const [balanceOption, setBalanceOption] = useState("add");
  const [creditBalanceOption, setCreditBalanceOption] = useState("add");
  const [walletBalanceOption, setWalletBalanceOption] = useState("add");
  const [activeSection, setActiveSection] = useState(
    checkForWalletMin(userData?.walletFlag || "No") ? "wallet" : "credit"
  );
  const [balanceData, setBalanceData] = useState({
    amount: 0,
    creditAmount: 0,
    walletAmount: 0,
    remarks: "",
    type: "Local",
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);

  useEffect(() => {
    if (!canUpdateBalance) {
      setShowConfirmModal(false);
      setIsConfirmed(false);
    }
  }, [canUpdateBalance]);

  const dispatch = useDispatch();
  const { isFetchingUserBalance } = useSelector(userSelector);
  const { user } = useSelector(authSelector);
  const isEnhancedBalanceRole = hasRoles("tenant,superadmin,system", user);
  const targetRole = (userData?.role || "").toString().toLowerCase().trim();
  const isTargetLowTier = targetRole === "user" || targetRole === "admin";
  const restrictToWalletFlag = isEnhancedBalanceRole && isTargetLowTier;
  const forcedSection = checkForWalletMin(userData?.walletFlag)
    ? "wallet"
    : "credit";

  const walletOrCreditLabel = checkForWalletMin(userData?.walletFlag)
    ? t("balanceUser.wallet")
    : t("balanceUser.credit");
  const addAmountLabel = `${t("balanceUser.add")} ${walletOrCreditLabel} ${t("balanceUser.amount")}`;
  const deductAmountLabel = `${t("balanceUser.deduct")} ${walletOrCreditLabel} ${t("balanceUser.amount")}`;

  const handleBalanceOption = (e) => {
    setBalanceOption(e.target.value);
  };

  useEffect(() => {
    if (isEnhancedBalanceRole) {
      setActiveSection(
        checkForWalletMin(userData?.walletFlag || "No") ? "wallet" : "credit"
      );
    }
  }, [isEnhancedBalanceRole, userData?.walletFlag]);

  const handleBalanaceSubmit = () => {

    setShowConfirmModal(true);
  };

  const executeBalanceUpdate = () => {
    const isWalletSubmit = activeSection === "wallet";
    const activeOption = isEnhancedBalanceRole
      ? isWalletSubmit
        ? walletBalanceOption
        : creditBalanceOption
      : balanceOption;
    const activeAmount = isEnhancedBalanceRole
      ? isWalletSubmit
        ? balanceData.walletAmount
        : balanceData.creditAmount
      : balanceData.amount;
    const amount = Number(activeAmount) || 0;
    const creditPayloadAmount = Number(balanceData.creditAmount) || 0;
    const walletPayloadAmount = Number(balanceData.walletAmount) || 0;
    const operation = activeOption === "add" ? "plus" : "minus";
    let balanceBody = {
      userId: userData?.id || id,
      type: balanceData.type,
      toBeAddedWallet: isEnhancedBalanceRole
        ? walletPayloadAmount
        : isWalletSubmit
          ? amount
          : 0,
      toBeAddedCredits: isEnhancedBalanceRole
        ? creditPayloadAmount
        : isWalletSubmit
          ? 0
          : amount,
      remark: balanceData.remarks,
      walletFlag: isWalletSubmit ? "Yes" : "No",
      operation,
    };

    const username = userData?.username || userData?.systemId || "User";

    dispatch(
      updateUserBalance({
        ...balanceBody,
      })
    ).then((action) => {

      const isSuccess =
        action.payload &&
        !action.error &&
        action.payload?.status?.toString().startsWith("2");

      middlewareUpdate(
        action,
        `User Balance for ${username}`,
        (action) => {
          setTimeout(
            () => window.dispatchEvent(new CustomEvent("refreshWallet")),
            500
          );
        },
        undefined,
        {
          modal: closeModal,
          refresh: () => {
            removeItemWithExpiry("usersListtxnmode");
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent("forceUserListUpdate"));
            }, 500);
          },
          noToast: isSuccess,
        }
      );

      if (isSuccess) {
        showToast(
          `${t("balanceUser.successfullyUpdated")} ${username}`,
          "success"
        );
      }
    });
  };
  return (
    <>
      <Modal show={modal} close={closeModal} justify="center">
        <div className="modal-header">
          <h6 className="modal-title d-flex gap-2 align-items-center fs-22">
            <img
              src={AppIcons.balance}
              alt=""
              style={{ width: 20, height: 20 }}
              loading="lazy"
            />
            {t("balanceUser.title")}
          </h6>
          <CloseButton theme="dark" onClick={closeModal} />
        </div>
        <div className="modal-body bg-white">
          {!isEnhancedBalanceRole && (
            <div
              className="header"
              style={{
                background: "#0000000a",
                borderRadius: "20px 20px 0 0",
                border: "1px solid #3341631A",
                padding: "20px 24px",
              }}
            >
              <FormSelect
                className="form-select bg-transparent fw-600"
                onChange={handleBalanceOption}
                value={balanceOption}
                disabled={addBalance}
              >
                <option value="add">{addAmountLabel}</option>
                <option value="deduct">{deductAmountLabel}</option>
              </FormSelect>
            </div>
          )}
          <div
            key={isEnhancedBalanceRole ? activeSection : balanceOption}
            className="p-4 border border-light_grey"
            style={{
              borderRadius: isEnhancedBalanceRole
                ? "20px"
                : "0 0 20px 20px",
            }}
          >
            {/* User and Current Balance in the same row */}
            <div className="row mb-3">
              <div className="col-6">
                <label className="form-label mt-2 fs-16 fw-600 text-dark d-inline-block me-2">
                  {t("balanceUser.userLabel")}:
                </label>
                <span className="fs-16 mt-2 d-inline-block">
                  {userData?.username || userData?.systemId || "User"}
                </span>
              </div>
              <div className="col-6">
                <label className="form-label mt-2 fs-16 fw-600 text-dark d-inline-block me-2">
                  {t("balanceUser.currentBalance")}:
                </label>
                <span className="fs-16 mt-2 d-inline-block">
                  {isEnhancedBalanceRole
                    ? activeSection === "wallet"
                      ? Number(userData?.wallet ?? 0).toFixed(2)
                      : Number(userData?.credit ?? 0).toFixed(2)
                    : checkForWalletMin(userData?.walletFlag)
                      ? Number(userData?.wallet ?? 0).toFixed(2)
                      : Number(userData?.credit ?? 0).toFixed(2)}
                </span>
              </div>
            </div>
            {isEnhancedBalanceRole ? (
              <>
                {(!restrictToWalletFlag || forcedSection === "credit") && (
                  <div className="mb-3 border border-light_grey br-12 overflow-hidden">
                    <button
                      type="button"
                      className="w-100 text-start bg-white border-0 px-3 py-2 fs-16 fw-600"
                      onClick={() => setActiveSection("credit")}
                    >
                      {`${t("balanceUser.add")} ${t(
                        "balanceUser.credit"
                      )} ${t("balanceUser.amount")}`}
                    </button>
                    {activeSection === "credit" && (
                      <div className="px-3 pb-3">
                        <div className="row mb-3">
                          <div className="col-3">
                            <label className="form-label mt-2 fs-16 fw-600 text-dark">
                              {t("balanceUser.amountLabel")}:
                            </label>
                          </div>
                          <div className="col-9">
                            <FormSelect
                              className="rounded form-select mb-2"
                              onChange={(e) =>
                                setCreditBalanceOption(e.target.value)
                              }
                              value={creditBalanceOption}
                              disabled={addBalance}
                            >
                              <option value="add">
                                {`${t("balanceUser.add")} ${t(
                                  "balanceUser.credit"
                                )} ${t("balanceUser.amount")}`}
                              </option>
                              <option value="deduct">
                                {`${t("balanceUser.deduct")} ${t(
                                  "balanceUser.credit"
                                )} ${t("balanceUser.amount")}`}
                              </option>
                            </FormSelect>
                            <input
                              type="number"
                              min={0}
                              className="rounded form-control"
                              placeholder={t("balanceUser.enterAmount")}
                              value={balanceData.creditAmount}
                              onChange={(e) =>
                                setBalanceData((prev) => ({
                                  ...prev,
                                  creditAmount: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {(!restrictToWalletFlag || forcedSection === "wallet") && (
                  <div className="mb-3 border border-light_grey br-12 overflow-hidden">
                    <button
                      type="button"
                      className="w-100 text-start bg-white border-0 px-3 py-2 fs-16 fw-600"
                      onClick={() => setActiveSection("wallet")}
                    >
                      {`${t("balanceUser.add")} ${t(
                        "balanceUser.wallet"
                      )} ${t("balanceUser.amount")}`}
                    </button>
                    {activeSection === "wallet" && (
                      <div className="px-3 pb-3">
                        <div className="row mb-3">
                          <div className="col-3">
                            <label className="form-label mt-2 fs-16 fw-600 text-dark">
                              {t("balanceUser.amountLabel")}:
                            </label>
                          </div>
                          <div className="col-9">
                            <FormSelect
                              className="rounded form-select mb-2"
                              onChange={(e) =>
                                setWalletBalanceOption(e.target.value)
                              }
                              value={walletBalanceOption}
                              disabled={addBalance}
                            >
                              <option value="add">
                                {`${t("balanceUser.add")} ${t(
                                  "balanceUser.wallet"
                                )} ${t("balanceUser.amount")}`}
                              </option>
                              <option value="deduct">
                                {`${t("balanceUser.deduct")} ${t(
                                  "balanceUser.wallet"
                                )} ${t("balanceUser.amount")}`}
                              </option>
                            </FormSelect>
                            <input
                              type="number"
                              min={0}
                              className="rounded form-control"
                              placeholder={t("balanceUser.enterAmount")}
                              value={balanceData.walletAmount}
                              onChange={(e) =>
                                setBalanceData((prev) => ({
                                  ...prev,
                                  walletAmount: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="row mb-3">
                <div className="col-3">
                  <label className="form-label mt-2 fs-16 fw-600 text-dark">
                    {t("balanceUser.amountLabel")}:
                  </label>
                </div>
                <div className="col-9">
                  <input
                    type="number"
                    min={0}
                    className="rounded form-control"
                    placeholder={t("balanceUser.enterAmount")}
                    value={balanceData.amount}
                    onChange={(e) =>
                      setBalanceData((prev) => ({
                        ...prev,
                        amount: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            )}
            {isEnhancedBalanceRole && (
              <div className="row mb-3">
                <div className="col-3">
                  <label className="form-label mt-2 fs-16 fw-600 text-dark">
                    {t("balanceUser.typeLabel")}:
                  </label>
                </div>
                <div className="col-9">
                  <FormSelect
                    className="rounded form-select"
                    value={balanceData.type}
                    onChange={(e) =>
                      setBalanceData((prev) => ({
                        ...prev,
                        type: e.target.value,
                      }))
                    }
                  >
                    <option value="Local">{t("balanceUser.local")}</option>
                    <option value="International">
                      {t("balanceUser.international")}
                    </option>
                  </FormSelect>
                </div>
              </div>
            )}
            <div className="row">
              <div className="col-3">
                <label className="form-label mt-2 fs-16 fw-600 text-dark">
                  {t("balanceUser.remarksLabel")}:
                </label>
              </div>
              <div className="col-9">
                <input
                  type="text"
                  className="rounded form-control"
                  placeholder={t("balanceUser.enterRemarks")}
                  value={balanceData.remarks}
                  onChange={(e) =>
                    setBalanceData((prev) => ({
                      ...prev,
                      remarks: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          </div>
        </div>
        <div className="modal-footer d-block bg-white">
          <div className="row g-3">
            <div className="col-6 mt-0">
              <button
                type="button"
                className="btn fs-16 fw-500 br-12 w-100 px-4 py-2 border_lightgray_1 me-2"
                onClick={() => {
                  setBalanceOption("add");
                  setCreditBalanceOption("add");
                  setWalletBalanceOption("add");
                  setBalanceData({
                    amount: 0,
                    creditAmount: 0,
                    walletAmount: 0,
                    remarks: "",
                    type: "Local",
                  });
                  if (isEnhancedBalanceRole) {
                    setActiveSection(
                      checkForWalletMin(userData?.walletFlag || "No")
                        ? "wallet"
                        : "credit"
                    );
                  }
                }}
              >
                {t("balanceUser.reset")}
              </button>
            </div>
            <div className="col-6 mt-0">
              <button
                type="submit"
                className="cbtn text-white btn w-100 fs-16 fw-500 br-12 px-4 py-2 border_lightgray_1"
                onClick={handleBalanaceSubmit}
                disabled={isFetchingUserBalance}
              >
                {t("balanceUser.apply")}
              </button>
            </div>
          </div>
        </div>
      </Modal>
      {showConfirmModal && (
        <Modal
          show={showConfirmModal}
          close={() => {
            setShowConfirmModal(false);
            setIsConfirmed(false);
          }}
          justify="center"
        >
          <div className="modal-header">
            <h6 className="modal-title fs-22">{t("balanceUser.confirmTitle")}</h6>
            <CloseButton
              theme="dark"
              onClick={() => {
                setShowConfirmModal(false);
                setIsConfirmed(false);
              }}
            />
          </div>
          <div className="modal-body py-2 px-3 bg-white">
            <div className="d-flex align-items-center my-3">
              <input
                className="form-check-input float-none me-2"
                type="checkbox"
                id="confirmCheck"
                checked={isConfirmed}
                onChange={(e) => setIsConfirmed(e.target.checked)}
              />
              <label
                className="form-check-label fs-18 fw-600 text-dark"
                htmlFor="confirmCheck"
              >
                {t("balanceUser.confirmCheckbox")}
                <span className="text-danger">*</span>
              </label>
            </div>
            <div className="d-flex align-items-center py-3 px-3 shadow br-8">
              <img src={danger} alt="danger" loading="lazy" />
              <div className="ps-3">
                <h6 className="text-primary fs-16 fw-600 mb-1">
                  {t("balanceUser.confirmMessage").replace(
                    "{action}",
                    (isEnhancedBalanceRole
                      ? activeSection === "wallet"
                        ? walletBalanceOption
                        : creditBalanceOption
                      : balanceOption) === "add"
                      ? t("balanceUser.add")
                      : t("balanceUser.deduct")
                  )}
                </h6>
                <p className="fs-16 fw-400 text-dark m-0">
                  {(isEnhancedBalanceRole
                    ? activeSection === "wallet"
                      ? walletBalanceOption
                      : creditBalanceOption
                    : balanceOption) === "add"
                    ? t("balanceUser.adding")
                    : t("balanceUser.deducting")}{" "}
                  {isEnhancedBalanceRole
                    ? activeSection === "wallet"
                      ? balanceData.walletAmount
                      : balanceData.creditAmount
                    : balanceData.amount}{" "}
                  {isEnhancedBalanceRole
                    ? activeSection === "wallet"
                      ? t("balanceUser.wallet")
                      : t("balanceUser.credit")
                    : checkForWalletMin(userData?.walletFlag)
                      ? t("balanceUser.wallet")
                      : t("balanceUser.credit")}{" "}
                  {(isEnhancedBalanceRole
                    ? activeSection === "wallet"
                      ? walletBalanceOption
                      : creditBalanceOption
                    : balanceOption) === "add"
                    ? t("balanceUser.to")
                    : t("balanceUser.from")}{" "}
                  <strong>
                    {userData?.username || userData?.systemId || "User"}
                  </strong>
                </p>
              </div>
            </div>
          </div>
          <div className="modal-footer d-block bg-white">
            <div className="row g-3">
              <div className="col-6 mt-0">
                <button
                  className="btn fs-16 fw-500 br-12 w-100 px-4 py-2 border_lightgray_1 me-2"
                  onClick={() => {
                    executeBalanceUpdate();
                    setShowConfirmModal(false);
                    setIsConfirmed(false);
                    closeModal();
                  }}
                  disabled={!isConfirmed}
                >
                  {t("balanceUser.confirm")}
                </button>
              </div>
              <div className="col-6 mt-0">
                <button
                  className="bg-primary-red text-white btn w-100 fs-16 fw-500 br-12 px-4 py-2 border_lightgray_1"
                  onClick={() => {
                    setShowConfirmModal(false);
                    setIsConfirmed(false);
                  }}
                >
                  {t("balanceUser.cancel")}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default BalanceUser;
